import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import {
  Mail, Save, Trash2, Send, AlertCircle, CheckCircle, X, Lock, Loader2, ExternalLink, Info, FileSignature
} from 'lucide-react';
import RichTextEditor from '../components/email/RichTextEditor';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROVIDER_PRESETS = {
  gmail: { smtp_host: 'smtp.gmail.com', smtp_port: 587, use_tls: true, label: 'Gmail' },
  outlook: { smtp_host: 'smtp.office365.com', smtp_port: 587, use_tls: true, label: 'Outlook / Microsoft 365' },
  custom: { smtp_host: '', smtp_port: 587, use_tls: true, label: 'Custom SMTP' }
};

const HELP_LINKS = {
  gmail: 'https://support.google.com/accounts/answer/185833',
  outlook: 'https://support.microsoft.com/en-us/account-billing/manage-app-passwords-for-two-step-verification-d6dc8c6d-4bf7-4851-ad95-6d07799387e9'
};

/**
 * DistributionListPicker — chip-style multi-select used in the Report
 * Defaults tab to choose the AMC's default To / CC recipients for the
 * Drawdown Report email modal.
 *
 * Restricted to active internal `amc_user` accounts (sourced from
 * /api/email-recipients). Free-text external emails are intentionally
 * NOT supported here — the per-send modal still allows external recipients
 * (for admins / permitted users), but distribution lists must stay internal.
 */
const DistributionListPicker = ({
  label, selected, roster, onAdd, onRemove, testidPrefix, helper,
}) => {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const wrapRef = React.useRef(null);
  React.useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  const selectedIds = new Set(selected.map(m => m.user_id));
  const q = search.toLowerCase().trim();
  const filtered = roster.filter(m => {
    if (selectedIds.has(m.user_id)) return false;
    if (!q) return true;
    return (m.name || '').toLowerCase().includes(q) || (m.email || '').toLowerCase().includes(q);
  });
  return (
    <div className="relative" ref={wrapRef} data-testid={`${testidPrefix}-picker`}>
      <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
      <div
        className="min-h-[40px] w-full border border-gray-300 rounded-lg px-2 py-1 flex flex-wrap items-center gap-1 focus-within:border-blue-400 bg-white cursor-text"
        onClick={() => setOpen(true)}
      >
        {selected.map(m => (
          <span
            key={m.user_id}
            className="inline-flex items-center text-xs rounded px-2 py-0.5 bg-blue-100 text-blue-800"
            data-testid={`${testidPrefix}-chip-${m.user_id}`}
          >
            {m.name || m.email}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onRemove(m.user_id); }}
              className="ml-1.5 text-current opacity-60 hover:opacity-100"
              aria-label={`Remove ${m.name || m.email}`}
              data-testid={`${testidPrefix}-remove-${m.user_id}`}
            >×</button>
          </span>
        ))}
        <input
          type="text"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={selected.length === 0 ? 'Click to pick internal team members…' : ''}
          className="flex-1 min-w-[120px] text-sm bg-transparent border-0 outline-none px-1 py-0.5"
          data-testid={`${testidPrefix}-search`}
        />
      </div>
      {open && (
        <div className="absolute z-30 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto" data-testid={`${testidPrefix}-dropdown`}>
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-xs text-gray-400">
              {roster.length === 0
                ? 'No internal team members found in this AMC.'
                : 'No matches. Search by name or email.'}
            </div>
          ) : filtered.map(m => (
            <button
              key={m.user_id}
              type="button"
              onClick={() => { onAdd(m); setSearch(''); }}
              className="w-full text-left px-3 py-1.5 text-sm hover:bg-blue-50 flex items-center justify-between"
              data-testid={`${testidPrefix}-option-${m.user_id}`}
            >
              <span className="font-medium text-gray-800">{m.name || m.email}</span>
              <span className="text-xs text-gray-500 ml-3">{m.email}</span>
            </button>
          ))}
        </div>
      )}
      {helper && <p className="text-[11px] text-gray-500 mt-1">{helper}</p>}
    </div>
  );
};

const EmailConfiguration = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [config, setConfig] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [activeTab, setActiveTab] = useState('smtp'); // smtp | signature
  const [signatureHtml, setSignatureHtml] = useState('');
  const [signatureSaving, setSignatureSaving] = useState(false);
  // Phase R — AMC-wide defaults for the Drawdown Report email.
  const [reportDefaults, setReportDefaults] = useState({
    subject: '', body: '', available_placeholders: [],
    default_to_members: [], default_cc_members: [],
    default_format: 'excel',
  });
  const [reportDefaultsSaving, setReportDefaultsSaving] = useState(false);
  // Roster of internal AMC users used to populate the distribution-list
  // pickers under the Report Defaults tab.
  const [distRoster, setDistRoster] = useState([]);

  const [form, setForm] = useState({
    provider: 'gmail',
    smtp_host: 'smtp.gmail.com',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    from_email: '',
    from_name: user?.name || '',
    use_tls: true
  });

  useEffect(() => {
    fetchConfig();
    fetchReportDefaults();
    // eslint-disable-next-line
  }, []);

  const fetchReportDefaults = async () => {
    try {
      const [{ data }, { data: roster }] = await Promise.all([
        axios.get(`${API_URL}/drawdown-report-email-defaults`),
        axios.get(`${API_URL}/email-recipients`).catch(() => ({ data: { members: [] } })),
      ]);
      setReportDefaults({
        subject: data.subject || '',
        body: data.body || '',
        available_placeholders: data.available_placeholders || [],
        default_to_members: data.default_to_members || [],
        default_cc_members: data.default_cc_members || [],
        default_format: data.default_format || 'excel',
      });
      setDistRoster(roster.members || []);
    } catch { /* tolerate — falls back to platform defaults */ }
  };

  const saveReportDefaults = async () => {
    setReportDefaultsSaving(true);
    try {
      const { data } = await axios.put(`${API_URL}/drawdown-report-email-defaults`, {
        subject: reportDefaults.subject,
        body: reportDefaults.body,
        default_to_user_ids: (reportDefaults.default_to_members || []).map(m => m.user_id),
        default_cc_user_ids: (reportDefaults.default_cc_members || []).map(m => m.user_id),
        default_format: reportDefaults.default_format || 'excel',
      });
      setReportDefaults({
        ...reportDefaults,
        subject: data.subject,
        body: data.body,
        default_to_members: data.default_to_members || [],
        default_cc_members: data.default_cc_members || [],
        default_format: data.default_format || 'excel',
      });
      setSuccess('Drawdown Report email defaults saved.');
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to save report defaults');
    } finally {
      setReportDefaultsSaving(false);
    }
  };

  // --- Distribution-list picker helpers (Report Defaults tab) -------------
  const addDistMember = (field, member) => {
    setReportDefaults(prev => {
      const cur = prev[field] || [];
      if (cur.some(m => m.user_id === member.user_id)) return prev;
      return { ...prev, [field]: [...cur, member] };
    });
  };
  const removeDistMember = (field, userId) => {
    setReportDefaults(prev => ({
      ...prev,
      [field]: (prev[field] || []).filter(m => m.user_id !== userId),
    }));
  };

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/email-config/me`);
      if (data) {
        setConfig(data);
        setSignatureHtml(data.signature_html || '');
        setForm({
          provider: data.provider || 'gmail',
          smtp_host: data.smtp_host || 'smtp.gmail.com',
          smtp_port: data.smtp_port || 587,
          smtp_username: data.smtp_username || '',
          smtp_password: '',  // never returned; keep blank
          from_email: data.from_email || '',
          from_name: data.from_name || user?.name || '',
          use_tls: data.use_tls !== false
        });
      } else {
        setConfig(null);
        setForm(f => ({
          ...f,
          smtp_username: user?.email || '',
          from_email: user?.email || '',
          from_name: user?.name || ''
        }));
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load email configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleProviderChange = (provider) => {
    const preset = PROVIDER_PRESETS[provider];
    setForm(f => ({
      ...f,
      provider,
      smtp_host: preset.smtp_host || f.smtp_host,
      smtp_port: preset.smtp_port,
      use_tls: preset.use_tls
    }));
  };

  const handleSave = async (e) => {
    e?.preventDefault();
    setError(''); setSuccess('');
    setSaving(true);
    try {
      const payload = { ...form };
      if (config && !form.smtp_password) {
        delete payload.smtp_password;  // keep existing
      }
      const { data } = await axios.put(`${API_URL}/email-config/me`, payload);
      setConfig(data);
      setForm(f => ({ ...f, smtp_password: '' }));
      setSuccess('Email configuration saved. Click "Test Connection" to verify.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save email configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setError(''); setSuccess('');
    setTesting(true);
    try {
      const { data } = await axios.post(`${API_URL}/email-config/me/test`);
      if (data.ok) {
        setSuccess('Connection successful! Your SMTP credentials are working.');
        await fetchConfig();
      } else {
        setError(data.error || 'Connection failed');
        await fetchConfig();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to test connection');
    } finally {
      setTesting(false);
    }
  };

  const handleDelete = async () => {
    setError(''); setSuccess('');
    try {
      await axios.delete(`${API_URL}/email-config/me`);
      setConfig(null);
      setConfirmDelete(false);
      setForm({
        provider: 'gmail',
        smtp_host: 'smtp.gmail.com',
        smtp_port: 587,
        smtp_username: user?.email || '',
        smtp_password: '',
        from_email: user?.email || '',
        from_name: user?.name || '',
        use_tls: true
      });
      setSuccess('Email configuration removed.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete email configuration');
    }
  };

  const handleSaveSignature = async () => {
    setError(''); setSuccess('');
    if (!config) {
      setError('Please save your SMTP configuration first.');
      return;
    }
    setSignatureSaving(true);
    try {
      // Send the upsert payload including the new signature; password stays unchanged.
      await axios.put(`${API_URL}/email-config/me`, {
        provider: config.provider,
        smtp_host: config.smtp_host,
        smtp_port: config.smtp_port,
        smtp_username: config.smtp_username,
        from_email: config.from_email,
        from_name: config.from_name,
        use_tls: config.use_tls,
        signature_html: signatureHtml,
      });
      setSuccess('Signature saved. It will be appended to all outgoing emails automatically.');
      await fetchConfig();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save signature');
    } finally { setSignatureSaving(false); }
  };

  const sendingAllowed = !!user?.email_sending_enabled;

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <Loader2 className="animate-spin text-blue-600" size={32} />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center" data-testid="email-config-title">
          <Mail className="mr-3" size={32} />
          Email Configuration
        </h1>
        <p className="text-gray-600 mt-1">
          Configure your own SMTP credentials so you can send emails (e.g. drawdown notices) from the portal under your own identity.
        </p>
      </div>

      {!sendingAllowed && (
        <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start" data-testid="email-config-disabled-notice">
          <AlertCircle className="text-yellow-600 mr-2 flex-shrink-0 mt-0.5" size={20} />
          <div className="text-sm text-yellow-900">
            <p className="font-semibold">Email sending is currently disabled for your account.</p>
            <p className="mt-1">You can still save and test SMTP credentials below, but you will not be able to send emails until an Admin enables “Allow this user to send emails” for you in <strong>User Management</strong>.</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-4">
        <div className="flex items-center gap-1">
          <button
            onClick={() => setActiveTab('smtp')}
            data-testid="email-config-tab-smtp"
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center ${activeTab === 'smtp' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-600 hover:text-gray-900'}`}
          >
            <Mail size={14} className="mr-2" /> SMTP Credentials
          </button>
          <button
            onClick={() => setActiveTab('signature')}
            data-testid="email-config-tab-signature"
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center ${activeTab === 'signature' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-600 hover:text-gray-900'}`}
          >
            <FileSignature size={14} className="mr-2" /> Email Signature
          </button>
          <button
            onClick={() => setActiveTab('report_defaults')}
            data-testid="email-config-tab-report-defaults"
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center ${activeTab === 'report_defaults' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-600 hover:text-gray-900'}`}
          >
            <Send size={14} className="mr-2" /> Report Email Defaults
          </button>
        </div>
      </div>

      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start" data-testid="email-config-success">
          <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-0.5" size={20} />
          <div className="flex-1 text-sm text-green-800">{success}</div>
          <button onClick={() => setSuccess('')} className="text-green-600 hover:text-green-700"><X size={16} /></button>
        </div>
      )}

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start" data-testid="email-config-error">
          <AlertCircle className="text-red-600 mr-2 flex-shrink-0 mt-0.5" size={20} />
          <div className="flex-1 text-sm text-red-800 whitespace-pre-wrap">{error}</div>
          <button onClick={() => setError('')} className="text-red-600 hover:text-red-700"><X size={16} /></button>
        </div>
      )}

      {/* Status card */}
      {activeTab === 'smtp' && config && (
        <div className="mb-6 p-4 bg-white border border-gray-200 rounded-lg" data-testid="email-config-status">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className={`h-3 w-3 rounded-full mr-3 ${config.is_verified ? 'bg-green-500' : 'bg-yellow-500'}`}></div>
              <div>
                <p className="text-sm font-semibold text-gray-900">
                  {config.is_verified ? 'Connection verified' : 'Saved but not yet verified'}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {config.smtp_username} · via {config.smtp_host}:{config.smtp_port}
                  {config.last_tested_at && ` · last tested ${new Date(config.last_tested_at).toLocaleString()}`}
                </p>
                {config.last_test_error && !config.is_verified && (
                  <p className="text-xs text-red-600 mt-1 max-w-2xl">⚠ {config.last_test_error}</p>
                )}
              </div>
            </div>
            <button
              onClick={() => setConfirmDelete(true)}
              className="text-red-600 hover:bg-red-50 p-2 rounded-md transition-colors flex items-center text-sm"
              data-testid="email-config-delete-btn"
            >
              <Trash2 size={16} className="mr-1" />
              Remove
            </button>
          </div>
        </div>
      )}

      <form onSubmit={handleSave} className={`bg-white border border-gray-200 rounded-lg p-6 space-y-5 ${activeTab !== 'smtp' ? 'hidden' : ''}`}>
        {/* Provider */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Email Provider</label>
          <div className="grid grid-cols-3 gap-3">
            {Object.entries(PROVIDER_PRESETS).map(([key, p]) => (
              <button
                type="button"
                key={key}
                onClick={() => handleProviderChange(key)}
                className={`px-4 py-3 border rounded-lg text-sm font-medium transition-colors ${
                  form.provider === key
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                }`}
                data-testid={`email-config-provider-${key}`}
              >
                {p.label}
              </button>
            ))}
          </div>
          {(form.provider === 'gmail' || form.provider === 'outlook') && (
            <a
              href={HELP_LINKS[form.provider]}
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center text-xs text-blue-600 hover:underline mt-2"
            >
              <ExternalLink size={12} className="mr-1" />
              How to generate an App Password for {PROVIDER_PRESETS[form.provider].label}
            </a>
          )}
        </div>

        {/* SMTP host / port */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">SMTP Host *</label>
            <input
              type="text" required
              value={form.smtp_host}
              disabled={form.provider !== 'custom'}
              onChange={(e) => setForm({ ...form, smtp_host: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
              data-testid="email-config-host"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Port *</label>
            <input
              type="number" required
              value={form.smtp_port}
              onChange={(e) => setForm({ ...form, smtp_port: parseInt(e.target.value || '0', 10) })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              data-testid="email-config-port"
            />
          </div>
        </div>

        {/* Username / Password */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">SMTP Username (your email) *</label>
            <input
              type="email" required
              value={form.smtp_username}
              onChange={(e) => setForm({ ...form, smtp_username: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              data-testid="email-config-username"
              placeholder="you@example.com"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {config ? 'App Password (leave blank to keep existing)' : 'App Password *'}
            </label>
            <div className="relative">
              <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="password"
                value={form.smtp_password}
                onChange={(e) => setForm({ ...form, smtp_password: e.target.value })}
                className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={config ? '••••••••••••' : 'xxxx xxxx xxxx xxxx'}
                required={!config}
                data-testid="email-config-password"
                autoComplete="new-password"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">Stored encrypted (Fernet) on the server. Never shown again after saving.</p>
          </div>
        </div>

        {/* From email/name */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">From Email *</label>
            <input
              type="email" required
              value={form.from_email}
              onChange={(e) => setForm({ ...form, from_email: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              data-testid="email-config-from-email"
              placeholder="you@example.com"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">From Name *</label>
            <input
              type="text" required
              value={form.from_name}
              onChange={(e) => setForm({ ...form, from_name: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              data-testid="email-config-from-name"
              placeholder="Your Name"
            />
          </div>
        </div>

        {/* TLS */}
        <label className="flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={form.use_tls}
            onChange={(e) => setForm({ ...form, use_tls: e.target.checked })}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            data-testid="email-config-tls"
          />
          <span className="ml-2 text-sm text-gray-700">Use STARTTLS (recommended)</span>
        </label>

        {/* Help banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start">
          <Info size={16} className="text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-blue-900">
            <strong>Tip:</strong> If your email provider uses 2-Factor Authentication (recommended), you must generate a dedicated <strong>App Password</strong> — your normal account password will not work. Use the link above for step-by-step instructions.
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap items-center justify-end gap-3 pt-2 border-t border-gray-100">
          <button
            type="button"
            onClick={handleTest}
            disabled={!config || testing}
            className="px-5 py-2 border border-blue-300 text-blue-700 rounded-lg hover:bg-blue-50 font-medium flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="email-config-test-btn"
          >
            {testing ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Send size={16} className="mr-2" />}
            Test Connection
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium flex items-center disabled:opacity-50"
            data-testid="email-config-save-btn"
          >
            {saving ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Save size={16} className="mr-2" />}
            {config ? 'Update Configuration' : 'Save Configuration'}
          </button>
        </div>
      </form>

      {/* Signature panel */}
      {activeTab === 'signature' && (
        <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4" data-testid="email-config-signature-panel">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-sm font-bold text-gray-900 flex items-center"><FileSignature size={16} className="mr-2 text-blue-600" /> Email Signature</h3>
              <p className="text-xs text-gray-500 mt-1">
                This signature is automatically appended to every email you send from the portal.
                If you'd like to place it somewhere specific in a template, drop the merge field <span className="font-mono px-1 py-0.5 bg-gray-100 rounded">{`{{sender_signature}}`}</span> into the body — the auto-append is then suppressed.
              </p>
            </div>
            <button
              onClick={handleSaveSignature}
              disabled={!config || signatureSaving}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium flex items-center disabled:opacity-50"
              data-testid="email-config-signature-save"
            >
              {signatureSaving ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Save size={14} className="mr-1.5" />}
              Save Signature
            </button>
          </div>
          {!config && (
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800 flex items-start">
              <AlertCircle size={14} className="text-yellow-600 mr-2 flex-shrink-0 mt-0.5" />
              Save your SMTP credentials first — your signature is stored alongside them.
            </div>
          )}
          <RichTextEditor
            value={signatureHtml}
            onChange={setSignatureHtml}
            minHeight={220}
            placeholder="Type your signature — name, title, AMC name, contact details, website..."
            testidPrefix="sig"
          />
          {/* Live preview */}
          <div className="border border-dashed border-gray-300 rounded p-4 bg-gray-50">
            <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-2">Preview as it appears in sent email</p>
            <div className="text-sm bg-white p-3 rounded border" dangerouslySetInnerHTML={{ __html: signatureHtml || '<em class="text-gray-400">(empty signature)</em>' }} />
          </div>
        </div>
      )}

      {activeTab === 'report_defaults' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-4" data-testid="report-defaults-panel">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-sm font-bold text-gray-900 flex items-center"><Send size={16} className="mr-2 text-blue-600" /> Drawdown Report Email — Defaults</h3>
              <p className="text-xs text-gray-500 mt-1">
                Set the AMC-wide defaults that pre-fill the Email Report modal in any Drawdown&apos;s Reports tab. The distribution list lets you send to your standard internal team in one click — no typing required.
              </p>
            </div>
            <button
              onClick={saveReportDefaults}
              disabled={reportDefaultsSaving}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium flex items-center disabled:opacity-50"
              data-testid="report-defaults-save"
            >
              {reportDefaultsSaving ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Save size={14} className="mr-1.5" />}
              Save Defaults
            </button>
          </div>

          {/* Distribution List — TO */}
          <DistributionListPicker
            label="Default To (distribution list)"
            field="default_to_members"
            selected={reportDefaults.default_to_members || []}
            roster={distRoster}
            onAdd={(m) => addDistMember('default_to_members', m)}
            onRemove={(uid) => removeDistMember('default_to_members', uid)}
            testidPrefix="dist-to"
            helper="These users auto-populate the To field every time someone opens the Email Report modal."
          />

          {/* Distribution List — CC */}
          <DistributionListPicker
            label="Default CC (optional)"
            field="default_cc_members"
            selected={reportDefaults.default_cc_members || []}
            roster={distRoster}
            onAdd={(m) => addDistMember('default_cc_members', m)}
            onRemove={(uid) => removeDistMember('default_cc_members', uid)}
            testidPrefix="dist-cc"
            helper="CC'd on every Drawdown Report email."
          />

          {/* Default Format */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Default attachment format</label>
            <div className="flex gap-2">
              {['excel', 'pdf'].map(f => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setReportDefaults({ ...reportDefaults, default_format: f })}
                  className={`px-3 py-1.5 text-xs rounded-lg border ${
                    (reportDefaults.default_format || 'excel') === f
                      ? 'bg-blue-50 border-blue-400 text-blue-700 font-medium'
                      : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                  }`}
                  data-testid={`report-defaults-format-${f}`}
                >
                  {f === 'excel' ? 'Excel (.xlsx)' : 'PDF (.pdf)'}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Subject template</label>
            <input
              type="text"
              value={reportDefaults.subject}
              onChange={(e) => setReportDefaults({ ...reportDefaults, subject: e.target.value })}
              placeholder="Drawdown Report — {drawdown_name} ({filter_label})"
              className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:border-blue-400 focus:outline-none"
              data-testid="report-defaults-subject"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Body template</label>
            <textarea
              rows={8}
              value={reportDefaults.body}
              onChange={(e) => setReportDefaults({ ...reportDefaults, body: e.target.value })}
              placeholder={'Hi team,\n\nPlease find attached the {filter_label} drawdown report for {drawdown_name}{fund_suffix}.\n\nRegards,\n{user_name}'}
              className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 font-mono focus:border-blue-400 focus:outline-none"
              data-testid="report-defaults-body"
            />
          </div>
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-xs">
            <div className="font-medium text-blue-900 mb-1.5 flex items-center"><Info size={13} className="mr-1.5" /> Available placeholders</div>
            <div className="flex flex-wrap gap-1.5">
              {(reportDefaults.available_placeholders || []).map(ph => (
                <code key={ph} className="px-1.5 py-0.5 bg-white border border-blue-200 rounded text-blue-700 font-mono">{ph}</code>
              ))}
            </div>
            <p className="text-blue-700 mt-2">Leave a field blank to use the platform default. Line breaks in the body are preserved in the email.</p>
          </div>
        </div>
      )}

      {/* Delete confirm modal */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center mb-4">
              <AlertCircle className="text-red-600 mr-2" size={24} />
              <h3 className="text-lg font-bold text-gray-900">Remove email configuration?</h3>
            </div>
            <p className="text-sm text-gray-600 mb-6">
              Your saved SMTP credentials will be deleted from this AMC. You will need to re-enter them (and your App Password) to send emails again.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setConfirmDelete(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
                data-testid="email-config-delete-confirm"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailConfiguration;
